import { useSchemaOrg } from '@unhead/schema-org/react';

/**
 * @type {import('$types/components/partials/hygiene').HygieneSchema}
 */

const HygieneSchema = ({ page, url }) => {
    if (!page) {
        useSchemaOrg([], { tagPosition: 'head' });
        return null;
    }

    // Use custom schema markup from CMS if provided
    if (page.schemaMarkup) {
        try {
            const customSchema = JSON.parse(page.schemaMarkup);
            useSchemaOrg(Array.isArray(customSchema) ? customSchema : [customSchema], { tagPosition: 'head' });
        } catch {
            useSchemaOrg([], { tagPosition: 'head' });
        }
        return null;
    }

    const schemas = [];

    // WebPage Schema for informational/policy pages
    const webPageSchema = {
        '@type': 'WebPage',
        name: page.title,
        url: url,
    };

    // Add description if available
    if (page.searchDescription) {
        webPageSchema.description = page.searchDescription;
    } else if (page.listingText) {
        webPageSchema.description = page.listingText;
    }

    // Add image if available
    if (page.listingImage?.src) {
        webPageSchema.image = {
            '@type': 'ImageObject',
            url: page.listingImage.src,
        };
    }

    // Publisher (RealEstateAgent)
    webPageSchema.publisher = {
        '@type': 'RealEstateAgent',
        name: 'Beauchamp Estates',
        url: url?.split('/').slice(0, 3).join('/')
    };

    // Date published
    if (page.firstPublishedAt) {
        webPageSchema.datePublished = page.firstPublishedAt;
    }

    // Date modified
    if (page.lastPublishedAt) {
        webPageSchema.dateModified = page.lastPublishedAt;
    } else if (page.lastModified) {
        webPageSchema.dateModified = page.lastModified;
    }

    // Main entity of page (canonical URL)
    if (page.canonicalUrl) {
        webPageSchema.mainEntityOfPage = {
            '@type': 'WebPage',
            '@id': page.canonicalUrl
        };
    } else if (url) {
        webPageSchema.mainEntityOfPage = {
            '@type': 'WebPage',
            '@id': url
        };
    }

    // Add breadcrumb if parent exists
    if (page.parent) {
        webPageSchema.breadcrumb = {
            '@type': 'BreadcrumbList',
            itemListElement: [
                {
                    '@type': 'ListItem',
                    position: 1,
                    name: 'Home',
                    item: url?.split('/').slice(0, 3).join('/')
                }
            ]
        };

        if (page.parent.title) {
            webPageSchema.breadcrumb.itemListElement.push({
                '@type': 'ListItem',
                position: 2,
                name: page.parent.title,
                item: page.parent.relativeUrl
                    ? `${url?.split('/').slice(0, 3).join('/')}${page.parent.relativeUrl}`
                    : url?.split('/').slice(0, 3).join('/')
            });
        }

        webPageSchema.breadcrumb.itemListElement.push({
            '@type': 'ListItem',
            position: webPageSchema.breadcrumb.itemListElement.length + 1,
            name: page.title,
            item: url
        });
    }

    // Extract text content from modules if available
    if (page.modules && page.modules.length > 0) {
        const textContent = page.modules
            .filter(module => module.__typename === 'HygieneSectionBlock')
            .map(module => {
                const heading = module.heading || '';
                const items = module.items?.map(item => {
                    const itemHeading = item?.heading || '';
                    const itemTexts = item?.items?.map(i => i?.text || '').join(' ') || '';
                    return `${itemHeading} ${itemTexts}`.trim();
                }).join(' ') || '';
                return `${heading} ${items}`.trim();
            })
            .join(' ')
            .replace(/\s+/g, ' ')
            .trim();

        if (textContent) {
            webPageSchema.text = textContent.substring(0, 5000); // Limit to reasonable length
        }
    }

    schemas.push(webPageSchema);

    useSchemaOrg(schemas, { tagPosition: 'head' });

    return null;
};

export default HygieneSchema;
